import { TagId } from "../RootPost";
import { UserRole } from "./permissions";
import { isNumber, merge } from "lodash";
import { hasItems } from "../utils/collections";
import spaceAdo from "./spaces/spaceAdo";
import { SpaceConfig } from "./spaces/spaceConfig";
import spaceCpp from "./spaces/spaceCpp";
import spaceDotnet from "./spaces/spaceDotnet";
import { SpaceId } from "./spaces/spaceIds";
import spaceInternal from "./spaces/spaceInternal";
import nonVSSpacesBase from "./spaces/nonVSSpacesBase";
import spaceTfs from "./spaces/spaceTfs";
import spaceVS from "./spaces/spaceVS";
import spaceVSMac from "./spaces/spaceVSMac";
import { TelemetryHelper } from "../telemetry/TelemetryHelper";
import spaceCloudMine from "./spaces/spaceCloudMine";

const spaceConfigFromId: Record<TagId, SpaceConfig> = {
    [spaceVS.id]: spaceVS,
    [spaceVSMac.id]: spaceVSMac,
    [spaceCpp.id]: spaceCpp,
    [spaceDotnet.id]: spaceDotnet,
    [spaceAdo.id]: spaceAdo,
    [spaceTfs.id]: spaceTfs,
    [spaceInternal.id]: spaceInternal,
    [spaceCloudMine.id]: spaceCloudMine,
};
export type SpaceItem = SpaceConfig | SpaceConfigGroup;
const spaceConfigFromLowercaseSlug: Record<string, SpaceConfig> = {};
// ordered list of spaces that show in site navigation, if permitted for current user
const navigationSpaces: SpaceItem[] = [];

let windowTitle: string = "";

export const INTERNAL_INSTANCE = "internal-instance";
export const EXTERNAL_INSTANCE = "external-instance";

export let instanceCssClassName: string = EXTERNAL_INSTANCE;
export const instance: OpenInstanceInfo | null = getServerConfig()?.instance;
export const appInsightsId: string | null = instance.appInsightsId;
export const server: OpenServerConfig | null = getServerConfig();
export type SpaceSlug = string;

function getServerConfig(): OpenServerConfig | null {
    // devcomServerConfig is set by server in Controllers\ConfigController.cs api/injectConfig API
    return ((window as any).devcomServerConfig as OpenServerConfig);
}

/** Space configuration coming from server - such as list of spaces, IDs and Slug for each
 * Used during initial load to resolve URLs like /devbox to the correct space.
 * nikolami: 2022-10-07: this will change over time to have more information about each space 
 * and will gradually replace hardcoded values from spaceConfig.ts with server-side values
 */
export interface OpenServerConfig {
    website: OpenWebsiteConfig;
    spaces: OpenSpaceConfig[];
    instance: OpenInstanceInfo;
}

export interface OpenInstanceInfo {
    isInternal: boolean;
    sendFeedbackUrl: string;
    downloadUrl: string;
    appInsightsId: string;
}

export interface SpaceGroup {
    key: string;
    displayName: string;
    spaces: SpaceSlug[];
}

/** Same as SpaceGropu, except containing SpaceConfig-s rather than just slugs */
export interface SpaceConfigGroup {
    key: string;
    displayName: string;
    spaces: SpaceConfig[];
}

export interface OpenWebsiteConfig {
    key: string;
    hostNames: string[];
    showSpaces: Array<SpaceSlug | SpaceGroup>;
    title: string;
}

export interface OpenSpaceConfig extends OpenSpaceConfigDatabaseJson {
    id: TagId;
    slug: SpaceSlug;
    displayName: string;
}

export interface OpenSpaceConfigDatabaseJson {
    basedOn?: SpaceSlug;

    /** Color to override site header and buttons/UI with. Used to recolor site based on space (e.g. Azure Portal vs Visual Studio spaces) */
    accentColor?: string;

    /** null to show in navigation to everyone, otherwise group names e.g. [MicrosoftEmployees, MVP] */
    showNavTo?: UserRole[];
}

export interface RestrictedServerConfig {
    websites: RestrictedWebsiteConfig[];
    websiteErrors: string[];
    spaces: RestrictedSpaceConfig[];
    spaceErrors: string[];
}

export interface RestrictedWebsiteConfig extends OpenWebsiteConfig {
}

export interface RestrictedSpaceConfig extends RestrictedSpaceConfigDatabaseJson {
    slug: SpaceSlug;
    displayName: string;
}

export interface RestrictedSpaceConfigDatabaseJson extends OpenSpaceConfigDatabaseJson {
    ado: RestrictedAdoInstanceConfig;

    /** null to show contents/posts to everyone, otherwise group name e.g. ["MicrosoftEmployees", "MVP"] */
    showContentTo?: string[];
}

export interface RestrictedAdoInstanceConfig {
    instance: string;
    defaultAreaPath: string;
}

//** Apply server configuration (such as what spaces to view in what order) on top of any existing hard-coded configuration */
export function loadServerConfig(telemetryHelper: TelemetryHelper): void {
    // check for errors in site server configuration - send by server as injected script in index.htm
    if (server == null) {
        const errorMessage = "Injected site configuration in window.devcomServerConfig is missing. It's used to show spaces and site themes."
        console.error(`ERR_INJECT_CONFIG: ${errorMessage}`);
        telemetryHelper.postFault("ERR_INJECT_CONFIG", errorMessage);
        return;
    }

    instanceCssClassName = server.instance.isInternal ? INTERNAL_INSTANCE : EXTERNAL_INSTANCE;

    if (instance.downloadUrl == null) {
        const errorMessage = `ERR_DOWNLOAD_URL: Missing instance.downloadUrl. Check Azure settings.`;
        console.error(errorMessage);
        telemetryHelper.postFault("ERR_DOWNLOAD_URL", errorMessage);
    }

    if (instance.sendFeedbackUrl == null) {
        const errorMessage = `ERR_SEND_FEEDBACK_URL: Missing instance.sendFeedbackUrl. Check Azure settings.`;
        console.error(errorMessage);
        telemetryHelper.postFault("ERR_SEND_FEEDBACK_URL", errorMessage);
    }

    if (instance.appInsightsId == null) {
        const errorMessage = `ERR_APP_INSIGHTS_ID: Missing instance.appInsightsId. Check Azure settings.`;
        console.error(errorMessage);
        telemetryHelper.postFault("ERR_APP_INSIGHTS_ID", errorMessage);
    }

    // create space dictionaries and override hard-coded space config using data from server
    server.spaces.forEach(space => {
        const knownConfig: SpaceConfig | null = spaceConfigFromId[space.id];

        // HACK: server does not send null/default values by design (smaller json to parse, easier to see and debug). 
        // In order for spread operator (...) to work below convert undefined values of properties to null.
        // Notes: 
        //  1. this only needs to be done for properties that are hardcoded in code
        //  2. REMOVE: Can be removed once we have no hardcoded config.
        if (space.showNavTo === undefined) {
            space.showNavTo = null;
        }
        if (space.accentColor === undefined) {
            space.accentColor = null;
        }

        // override values in knownConfig, if any, with the server config
        const baseConfig = knownConfig ?? nonVSSpacesBase;
        const mergedConfig: SpaceConfig = {
            ...baseConfig,
            ...space,
            reportProblemConfig: {
                ...baseConfig.reportProblemConfig,
            },
            reportSuggestionConfig: {
                ...baseConfig.reportSuggestionConfig,
            },
        };

        spaceConfigFromId[space.id] = mergedConfig;
        if (space.slug) {
            spaceConfigFromLowercaseSlug[space.slug.toLowerCase()] = mergedConfig;
        }
    });

    // determine order of spaces to show in nav
    const website: OpenWebsiteConfig | null = server.website;
    if (website) {
        // match found
        windowTitle = website.title;

        // get list of spaces to show in nav
        for (const item of website.showSpaces) {
            // if item is a string, it's a space slug, otherwise it's a SpaceGroup
            if (typeof item === "string") {
                const spaceSlug: string = item;
                const spaceConfig: SpaceConfig | null = spaceConfigFromLowercaseSlug[spaceSlug.toLowerCase()];
                if (spaceConfig) {
                    navigationSpaces.push(spaceConfig);
                }
                else {
                    const errorMessage = `Missing space config for slug: '${spaceSlug}'`;
                    console.error(`ERR_MISSING_SPACE_CONFIG: ${errorMessage}`);
                    telemetryHelper.postFault("ERR_MISSING_SPACE_CONFIG", errorMessage);
                }
            }
            else {
                // item is a SpaceGroup
                const childSpaces: SpaceConfig[] = [];
                for (const spaceSlug of item.spaces) {
                    const spaceConfig: SpaceConfig | null = spaceConfigFromLowercaseSlug[spaceSlug.toLowerCase()];
                    if (spaceConfig) {
                        childSpaces.push(spaceConfig);
                    }
                    else {
                        const errorMessage = `Missing space config for slug: '${spaceSlug}'`;
                        console.error(`ERR_MISSING_CHILD_SPACE_CONFIG: ${errorMessage}`);
                        telemetryHelper.postFault("ERR_MISSING_CHILD_SPACE_CONFIG", errorMessage);
                    }
                }
                navigationSpaces.push({ key: item.key, displayName: item.displayName, spaces: childSpaces });
            }
        }
    }
}

export function getSlugFromSpaceId(spaceId: SpaceId): string {
    const slug: string | null = spaceConfigFromId[spaceId]?.slug;

    return slug ?? `space_${spaceId}`;
}

export function getSpaceIdFromSlug(slug: string | null): SpaceId | null {
    if (!slug) {
        return null;
    }

    return spaceConfigFromLowercaseSlug[slug.toLowerCase()]?.id;
}

export function getSpaceNameFromSlug(slug: string | null): string | null {
    if (!slug) {
        return null;
    }

    return spaceConfigFromLowercaseSlug[slug.toLowerCase()]?.displayName;
}

export const getSpaceConfig = (spaceId: SpaceId): SpaceConfig | null => {
    if (!isNumber(spaceId)) {
        return null;
    }
    // eslint-disable-next-line security/detect-object-injection
    return spaceConfigFromId[spaceId];
}

export const getApplicableSpaces = (userRoles: UserRole[]): SpaceItem[] => {
    const result: SpaceItem[] = [];

    for (const item of navigationSpaces) {
        // if space has child spaces, check if user is allowed to view any of them
        if ((item as SpaceConfigGroup).spaces) {
            const group: SpaceConfigGroup = item as SpaceConfigGroup;
            const childSpaces: SpaceConfig[] = [];
            for (const space of group.spaces) {
                if ((space.showNavTo == null) || containsAny(userRoles, space.showNavTo)) {
                    childSpaces.push(space);
                }
            }

            if (childSpaces.length > 0) {
                // add group since it has at least one child space that user is allowed to view
                result.push({ key: group.key, displayName: group.displayName, spaces: childSpaces });
            }
        }
        else {
            // if no child spaces, check if user is allowed to view this space
            const space: SpaceConfig = item as SpaceConfig;

            // if no role restrictions, or the user is allowed to view space in nav
            if ((space.showNavTo == null) || containsAny(userRoles, space.showNavTo)) {
                result.push(space);
            }
        }
    }

    return result;
}

function containsAny(roles: UserRole[], checkForAny: UserRole[]): boolean {
    return hasItems(checkForAny) && checkForAny.some(role => roles.includes(role));
}

export const getWindowTitle = (): string => {
    return windowTitle;
}